// /src/components/ServiceStatus.js
//
// Client-facing QA monitor. Shows service health and delivery quality without
// exposing sending volumes: the backend returns banded levels ("≥ 99%") rather
// than counts, so there is nothing to redact here.
import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Chip,
  Divider,
  Stack,
  CircularProgress,
  Alert,
  Tooltip,
  IconButton,
} from "@mui/material";
import { MdCheckCircle, MdWarning, MdError, MdHelpOutline, MdRefresh } from "react-icons/md";
import { useTranslation } from "react-i18next";
import { fetchServiceStatus } from "../store/actions/statusActions";

const REFRESH_INTERVAL_MS = 60 * 1000;

// Every level the API can return maps to exactly one visual treatment. Keeping
// this as a lookup rather than nested ternaries means an unrecognised level
// falls through to "unknown" instead of silently rendering as healthy.
const LEVELS = {
  operational: { color: "success", Icon: MdCheckCircle },
  degraded: { color: "warning", Icon: MdWarning },
  disrupted: { color: "error", Icon: MdError },
  unknown: { color: "default", Icon: MdHelpOutline },
};

const levelOf = (level) => LEVELS[level] || LEVELS.unknown;

const StatusChip = ({ level, t }) => {
  const { color, Icon } = levelOf(level);
  return (
    <Chip
      size="small"
      color={color}
      variant={color === "default" ? "outlined" : "filled"}
      icon={<Icon style={{ fontSize: "1rem" }} />}
      label={t(`status.levels.${LEVELS[level] ? level : "unknown"}`)}
    />
  );
};

const ServiceStatus = () => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const next = await fetchServiceStatus();
      setData(next);
      setError("");
    } catch (e) {
      setError(t("status.fetchError"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
    const timer = setInterval(load, REFRESH_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [load]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const overall = levelOf(data?.overall);
  const OverallIcon = overall.Icon;

  return (
    <Box sx={{ maxWidth: 900, mx: "auto", p: { xs: 2, md: 4 } }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h5" component="h1">
          {t("status.title")}
        </Typography>
        <Tooltip title={t("status.refresh")}>
          <IconButton onClick={load} size="small" aria-label={t("status.refresh")}>
            <MdRefresh />
          </IconButton>
        </Tooltip>
      </Stack>

      {error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Headline banner */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 2,
          border: "1px solid",
          borderColor: overall.color === "default" ? "divider" : `${overall.color}.main`,
          bgcolor: overall.color === "default" ? "background.paper" : `${overall.color}.main`,
          color: overall.color === "default" ? "text.primary" : "#fff",
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <OverallIcon style={{ fontSize: "2.25rem", flexShrink: 0 }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {t(`status.overall.${LEVELS[data?.overall] ? data.overall : "unknown"}`)}
            </Typography>
            {data?.updatedAt && (
              <Typography variant="body2" sx={{ opacity: 0.85 }}>
                {t("status.lastChecked", { time: new Date(data.updatedAt).toLocaleString() })}
              </Typography>
            )}
          </Box>
        </Stack>
      </Paper>

      {/* Component health */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 2, border: "1px solid", borderColor: "divider" }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
          {t("status.components.title")}
        </Typography>
        {data?.components ? (
          <Stack divider={<Divider flexItem />} spacing={0}>
            {data.components.map((c) => (
              <Stack
                key={c.key}
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ py: 1.5 }}
              >
                <Typography variant="body2">{t(`status.components.${c.key}`)}</Typography>
                <StatusChip level={c.status} t={t} />
              </Stack>
            ))}
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {t("status.components.unavailable")}
          </Typography>
        )}
      </Paper>

      {/* Delivery quality */}
      <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: "1px solid", borderColor: "divider" }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
          {t("status.delivery.title")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t("status.delivery.subtitle")}
        </Typography>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          {["last24h", "last7d"].map((key) => {
            const entry = data?.delivery?.[key];
            const lv = levelOf(entry?.level);
            return (
              <Box
                key={key}
                sx={{
                  flex: 1,
                  p: 2,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  textAlign: "center",
                }}
              >
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase" }}>
                  {t(`status.delivery.${key}`)}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{ my: 1, fontWeight: 600, color: lv.color === "default" ? "text.secondary" : `${lv.color}.main` }}
                >
                  {/* No band means too little traffic to characterise - say so
                      rather than print a rate derived from a handful of messages. */}
                  {entry?.band || "—"}
                </Typography>
                <StatusChip level={entry?.level} t={t} />
                {!entry?.band && (
                  <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                    {t("status.delivery.insufficient")}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Stack>

        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2 }}>
          {t("status.delivery.footnote", { minutes: data?.settleMinutes ?? 30 })}
        </Typography>
      </Paper>

      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 3, textAlign: "center" }}>
        {t("status.monitoringNote")}
      </Typography>
    </Box>
  );
};

export default ServiceStatus;
